import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

import DetailsHeaderContainer from '@/components/ui/DetailsHeaderContainer/DetailsHeaderContainer';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';

import type { PublicSignerTimestamp } from '../publicSignerTypes';
import { formatExpiryCountdown } from '../utils/presentation';

import { PublicSignerSummaryItem } from './PublicSignerSummaryItem';

type PublicSignerDocumentHeaderProps = {
  canZoomIn: boolean;
  canZoomOut: boolean;
  currentPage: number;
  facilityName: string | null;
  fieldProgressLabel: string;
  isFieldProgressComplete: boolean;
  numPages: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  pdfZoom: number;
  requestTitle: string | null;
  sessionExpiresAt: PublicSignerTimestamp;
  signerName: string | null;
};

const PublicSignerHeader = styled(DetailsHeaderContainer)(({ theme }) => ({
  alignItems: 'center',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 16,
  flex: '0 0 auto',
  flexWrap: 'nowrap',
  gap: '12px',
  marginBottom: 0,
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: theme.zIndex.appBar,
  [theme.breakpoints.up('md')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
  },
  '.label': {
    maxHeight: 32,
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  '.sub-label': {
    height: 18,
    whiteSpace: 'nowrap'
  }
}));

const MobileSignerHeader = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 16,
  flex: '0 0 auto',
  gap: theme.spacing(0.75),
  padding: theme.spacing(1.25, 1.5),
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: theme.zIndex.appBar
}));

const MobileTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  flex: '0 1 auto',
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '20px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

const MobileIdentityRow = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0,
  overflow: 'hidden'
}));

const MobileMetaRow = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0
}));

const MobileFooterRow = styled(Stack)({
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: 0,
  width: '100%'
});

const MobileDetailText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flex: '0 1 auto',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

const MobileDetailSeparator = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.text.disabled,
  borderRadius: '50%',
  flex: '0 0 auto',
  height: 3,
  width: 3
}));

const MobileExpiryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.secondary,
  '& .MuiChip-label': {
    fontSize: 12,
    fontWeight: 700
  }
}));

const HeaderSummary = styled(Box)({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden'
});

const SummaryItems = styled(Stack)(({ theme }) => ({
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  minWidth: 0,
  rowGap: theme.spacing(0.5)
}));

const SummaryCell = styled(Box)(({ theme }) => ({
  flex: '0 1 auto',
  maxWidth: 180,
  minWidth: 0,
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    maxWidth: 220
  }
}));

const SecondarySummaryCell = styled(SummaryCell)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block'
  }
}));

const DocumentSummaryCell = styled(SummaryCell)(({ theme }) => ({
  maxWidth: 220,
  [theme.breakpoints.up('md')]: {
    maxWidth: 280
  }
}));

const FixedSummaryCell = styled(Box)({
  flex: '0 0 auto',
  minWidth: 'max-content',
  overflow: 'hidden'
});

const DesktopSummaryCell = styled(FixedSummaryCell)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block'
  }
}));

const HeaderControls = styled(Stack)({
  alignItems: 'center',
  flexShrink: 0,
  gap: 8,
  justifyContent: 'flex-end',
  marginLeft: 'auto'
});

const ZoomControls = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(0.25)
}));

const ZoomControlButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary
}));

const ZoomOutIcon = styled(Remove)({
  fontSize: 16
});

const ZoomInIcon = styled(Add)({
  fontSize: 16
});

const ZoomValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 500,
  minWidth: 44,
  textAlign: 'center'
}));

const FieldProgressChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isComplete'
})<{ isComplete: boolean }>(({ theme, isComplete }) => {
  const backgroundColor = isComplete
    ? theme.palette.success.main
    : theme.palette.warning.main;

  return {
    backgroundColor,
    color: theme.palette.getContrastText(backgroundColor),
    width: 'fit-content',
    '& .MuiChip-label': {
      fontSize: 12,
      fontWeight: 700
    }
  };
});

const PageCountChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.info.main, 0.7),
  color: theme.palette.text.secondary,
  '& .MuiChip-label': {
    fontSize: 12,
    fontWeight: 600
  }
}));

export function PublicSignerDocumentHeader({
  canZoomIn,
  canZoomOut,
  currentPage,
  facilityName,
  fieldProgressLabel,
  isFieldProgressComplete,
  numPages,
  onZoomIn,
  onZoomOut,
  pdfZoom,
  requestTitle,
  sessionExpiresAt,
  signerName
}: PublicSignerDocumentHeaderProps) {
  const trimmedFacilityName = facilityName?.trim();
  const isMobileViewport = useIsMobileViewport();
  const documentTitle = requestTitle?.trim() || 'Signature request';
  const expiryLabel = formatExpiryCountdown(sessionExpiresAt);
  const signerLabel = signerName?.trim() || 'Signer';

  if (isMobileViewport) {
    return (
      <MobileSignerHeader>
        <MobileIdentityRow direction="row">
          <MobileTitle>{documentTitle}</MobileTitle>
          <MobileDetailSeparator />
          {trimmedFacilityName ? (
            <>
              <MobileDetailText aria-label={`Facility: ${trimmedFacilityName}`}>
                {trimmedFacilityName}
              </MobileDetailText>
              <MobileDetailSeparator />
            </>
          ) : null}
          <MobileDetailText aria-label={`Signer name: ${signerLabel}`}>
            {signerLabel}
          </MobileDetailText>
        </MobileIdentityRow>
        <MobileFooterRow direction="row" gap={1}>
          <MobileMetaRow direction="row">
            <MobileExpiryChip
              aria-label={`Link expires in ${expiryLabel}`}
              label={expiryLabel}
              size="small"
            />
            <FieldProgressChip
              isComplete={isFieldProgressComplete}
              label={fieldProgressLabel}
              size="small"
            />
          </MobileMetaRow>
          <HeaderControls direction="row">
            <ZoomControls direction="row">
              <ZoomControlButton
                aria-label="Zoom out document"
                disabled={!canZoomOut}
                onClick={onZoomOut}
                size="small"
              >
                <ZoomOutIcon />
              </ZoomControlButton>
              <ZoomValue variant="body2">{pdfZoom}%</ZoomValue>
              <ZoomControlButton
                aria-label="Zoom in document"
                disabled={!canZoomIn}
                onClick={onZoomIn}
                size="small"
              >
                <ZoomInIcon />
              </ZoomControlButton>
            </ZoomControls>
            <PageCountChip
              aria-label={`Page ${currentPage} of ${numPages || 'unknown'}`}
              label={`${currentPage}/${numPages || '—'}`}
              size="small"
              variant="filled"
            />
          </HeaderControls>
        </MobileFooterRow>
      </MobileSignerHeader>
    );
  }

  return (
    <PublicSignerHeader>
      <HeaderSummary>
        <SummaryItems direction="row" useFlexGap>
          <DocumentSummaryCell>
            <PublicSignerSummaryItem label="Document" value={documentTitle} />
          </DocumentSummaryCell>
          {trimmedFacilityName ? (
            <SecondarySummaryCell>
              <PublicSignerSummaryItem
                label="Facility"
                value={trimmedFacilityName}
              />
            </SecondarySummaryCell>
          ) : null}
          <SecondarySummaryCell>
            <PublicSignerSummaryItem label="Signer name" value={signerLabel} />
          </SecondarySummaryCell>
          <FixedSummaryCell>
            <PublicSignerSummaryItem
              label="Link expires in"
              value={expiryLabel}
            />
          </FixedSummaryCell>
          <DesktopSummaryCell>
            <PublicSignerSummaryItem
              label="Fields"
              value={
                <FieldProgressChip
                  isComplete={isFieldProgressComplete}
                  label={fieldProgressLabel}
                  size="small"
                />
              }
            />
          </DesktopSummaryCell>
        </SummaryItems>
      </HeaderSummary>
      <HeaderControls direction="row">
        <ZoomControls direction="row">
          <ZoomControlButton
            aria-label="Zoom out document"
            disabled={!canZoomOut}
            onClick={onZoomOut}
            size="small"
          >
            <ZoomOutIcon />
          </ZoomControlButton>
          <ZoomValue variant="body2">{pdfZoom}%</ZoomValue>
          <ZoomControlButton
            aria-label="Zoom in document"
            disabled={!canZoomIn}
            onClick={onZoomIn}
            size="small"
          >
            <ZoomInIcon />
          </ZoomControlButton>
        </ZoomControls>
        <PageCountChip
          aria-label={`Page ${currentPage} of ${numPages || 'unknown'}`}
          label={`${currentPage}/${numPages || '—'}`}
          size="small"
          variant="filled"
        />
      </HeaderControls>
    </PublicSignerHeader>
  );
}
